import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import clsx from 'clsx';
import { api } from '../lib/api';
import { RefreshCw, AlertTriangle, BookOpen, Check, Info } from 'lucide-react';
import toast from 'react-hot-toast';
import SubjectProgressOverview from './SubjectProgressOverview';
import { useAuth } from '../hooks/useAuth';
import { Button, Modal, Tile, TileAccentDot, TileContent, TileDescription, TileTitle } from './ui';

// Neue Komponente für einzelne Aufgaben-Kacheln
interface TaskTileProps {
  task: Task;
  sessionId: number;
  unid: string;
  taskColor: string;
  isCompleted: boolean; // NEU: Status wird von Parent übergeben
  onStatusChange: (aid: number, isCompleted: boolean) => void;
}

function TaskTile({ task, sessionId, unid, taskColor, isCompleted, onStatusChange }: TaskTileProps) {
  const { user } = useAuth();
  const [showModal, setShowModal] = useState(false);
  const [taskDetails, setTaskDetails] = useState<any>(null);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [displayTeacher, setDisplayTeacher] = useState<string | null>(task.BelegLehrer ?? null);

  useEffect(() => {
    setDisplayTeacher(task.BelegLehrer ?? null);
  }, [task.BelegLehrer]);

  // ENTFERNT: Einzelne Task-Status Query - wird jetzt über Batch-Query gehandhabt

  // Funktion zum Laden der Aufgaben-Details
  const loadTaskDetails = async () => {
    setIsLoadingDetails(true);
    try {
      const response = await api.get(`/lernbuero/task-details/${task.AID}`);
      setTaskDetails(response.data.data);
    } catch (error) {
      console.error('Fehler beim Laden der Aufgaben-Details:', error);
      toast.error('Fehler beim Laden der Aufgaben-Details');
    } finally {
      setIsLoadingDetails(false);
    }
  };

  const handleInfoClick = () => {
    setShowModal(true);
    if (!taskDetails) {
      loadTaskDetails();
    }
  };

  // Mutation für das Markieren einer Aufgabe als erledigt
  const completeTaskMutation = useMutation(
    async () => {
      const response = await api.post('/lernbuero/tasks/complete', {
        sessionId,
        unid,
        aid: task.AID,
        lpid: task.LPID
      });
      return response.data;
    },
    {
      onSuccess: (data) => {
        onStatusChange(task.AID, true);
        setDisplayTeacher(user?.username || task.BelegLehrer || null);
        toast.success('Aufgabe als erledigt markiert');
        const initialized = data?.skillmap?.initialized;
        if (initialized && initialized > 0) {
          toast.success(`Kompetenzmap aktualisiert (${initialized} neue Kompetenzen)`);
        }
        const evidenceCreated = data?.competencyEvidence?.created;
        if (evidenceCreated && evidenceCreated > 0) {
          toast.success(`Kompetenz-Nachweis gespeichert (${evidenceCreated})`);
        }
      },
      onError: (error) => {
        console.error('Fehler beim Markieren der Aufgabe:', error);
        toast.error('Fehler beim Markieren der Aufgabe');
      }
    }
  );

  // Mutation für das Entfernen der Markierung einer Aufgabe
  const uncompleteTaskMutation = useMutation(
    async () => {
      const response = await api.delete('/lernbuero/tasks/complete', {
        data: {
          sessionId,
          unid,
          aid: task.AID
        }
      });
      return response.data;
    },
    {
      onSuccess: (data) => {
        onStatusChange(task.AID, false);
        setDisplayTeacher(null);
        toast.success('Aufgabe als nicht erledigt markiert');
        const removedCompetencies = data?.competencyEvidenceRemoved;
        if (removedCompetencies && removedCompetencies > 0) {
          toast.success(`Kompetenz-Nachweise entfernt (${removedCompetencies})`);
        }
      },
      onError: (error) => {
        console.error('Fehler beim Entfernen der Markierung:', error);
        toast.error('Fehler beim Entfernen der Markierung');
      }
    }
  );

  const handleTaskToggle = () => {
    if (completeTaskMutation.isLoading || uncompleteTaskMutation.isLoading) return;
    if (isCompleted) {
      uncompleteTaskMutation.mutate();
    } else {
      completeTaskMutation.mutate();
    }
  };

  const busy = completeTaskMutation.isLoading || uncompleteTaskMutation.isLoading;
  /** Beleg in der DB (auch vor Batch-Status sichtbar über BelegLehrer) */
  const hasBeleg = isCompleted || Boolean(task.BelegLehrer?.trim());

  return (
    <div className="relative group">
      <Tile
        variant="interactive"
        onClick={handleTaskToggle}
        className={clsx(
          'min-h-[5.25rem] select-none border-2',
          busy && 'pointer-events-none opacity-60',
          hasBeleg
            ? 'border-green-500 bg-green-50 shadow-sm dark:border-green-600 dark:bg-green-950/45'
            : 'border-gray-200 bg-gray-50 dark:border-gray-600 dark:bg-gray-800/80'
        )}
        style={hasBeleg ? { backgroundColor: `${taskColor}33` } : undefined}
        aria-busy={busy}
        aria-pressed={hasBeleg}
      >
        <TileAccentDot accentHex={taskColor} />
        <TileContent className="flex min-h-[5.25rem] flex-col items-center justify-center px-2 py-2 pt-5">
          <TileTitle
            as="p"
            className={clsx(
              'line-clamp-3 text-center text-xs font-medium leading-snug',
              hasBeleg
                ? 'text-green-800 line-through dark:text-green-200'
                : 'text-gray-900 dark:text-gray-50'
            )}
          >
            {task.Kurzberzeichnung}
          </TileTitle>
          {hasBeleg && displayTeacher && (
            <TileDescription className="mt-0.5 max-w-[8rem] truncate text-xs text-green-700 dark:text-green-300">
              {displayTeacher}
            </TileDescription>
          )}
          <div className="pointer-events-none absolute bottom-1.5 right-1.5">
            {hasBeleg ? (
              <Check className="h-4 w-4 text-green-600 dark:text-green-400" aria-hidden />
            ) : (
              <div
                className="h-4 w-4 rounded border-2 border-gray-300 dark:border-gray-500"
                aria-hidden
              />
            )}
          </div>
        </TileContent>
        <Button
          type="button"
          size="xs"
          variant="outline"
          className="absolute right-1 top-1 z-[2] h-7 min-h-0 w-7 min-w-0 p-0"
          title="Aufgaben-Details anzeigen"
          onClick={(e) => {
            e.stopPropagation();
            handleInfoClick();
          }}
        >
          <Info className="h-3.5 w-3.5" aria-hidden />
        </Button>
      </Tile>

      <Modal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title="Aufgaben-Details"
        size="lg"
        panelClassName="max-h-[90vh] overflow-y-auto"
        footer={
          <div className="flex justify-end">
            <Button type="button" variant="secondary" onClick={() => setShowModal(false)}>
              Schließen
            </Button>
          </div>
        }
      >
        {isLoadingDetails ? (
          <div className="flex items-center justify-center py-8">
            <RefreshCw className="h-8 w-8 animate-spin text-primary-600 dark:text-primary-400" />
            <span className="ml-3 text-sm text-gray-600 dark:text-gray-400">Lade Details…</span>
          </div>
        ) : taskDetails ? (
          <div className="space-y-4 text-sm">
            <div>
              <p className="mb-1 text-xs font-medium text-gray-700 dark:text-gray-300">AID</p>
              <p className="rounded border border-gray-200 bg-gray-50 p-2 text-gray-900 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100">
                {taskDetails.AID}
              </p>
            </div>
            <div>
              <p className="mb-1 text-xs font-medium text-gray-700 dark:text-gray-300">Kurzbezeichnung</p>
              <p className="rounded border border-gray-200 bg-gray-50 p-2 text-gray-900 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100">
                {taskDetails.Kurzberzeichnung}
              </p>
            </div>
            <div>
              <p className="mb-1 text-xs font-medium text-gray-700 dark:text-gray-300">Aufgabenstellung</p>
              <p className="whitespace-pre-wrap rounded border border-gray-200 bg-gray-50 p-2 text-gray-900 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100">
                {taskDetails.Aufgabenstellung || 'Keine Aufgabenstellung verfügbar'}
              </p>
            </div>
            <div>
              <p className="mb-1 text-xs font-medium text-gray-700 dark:text-gray-300">Link</p>
              {taskDetails.Link ? (
                <a
                  href={taskDetails.Link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="break-all text-primary-600 underline hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300"
                >
                  {taskDetails.Link}
                </a>
              ) : (
                <p className="text-sm italic text-gray-500 dark:text-gray-400">Kein Link verfügbar</p>
              )}
            </div>
            <div>
              <p className="mb-1 text-xs font-medium text-gray-700 dark:text-gray-300">SForm</p>
              <p className="rounded border border-gray-200 bg-gray-50 p-2 text-gray-900 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-100">
                {taskDetails.SForm || 'Keine SForm verfügbar'}
              </p>
            </div>
          </div>
        ) : (
          <div className="py-8 text-center text-sm text-gray-500 dark:text-gray-400">Keine Details verfügbar</div>
        )}
      </Modal>
    </div>
  );
}

interface Task {
  AID: number;
  LPID: number;
  Kurzberzeichnung: string;
  Sortierung: number;
  BelegLehrer?: string | null;
}

interface LearningPlan {
  LPID: number;
  NAME: string;
  START_DATUM: string;
  END_DATUM: string;
}

interface TasksData {
  tasks: Task[];
  learningPlans: LearningPlan[];
  completedTasks: number[];
}

const PANEL_B_QUICK_SUBJECTS = ['Deutsch', 'Mathematik', 'Englisch'] as const;
type PanelBQuickSubject = (typeof PANEL_B_QUICK_SUBJECTS)[number];

interface LernbueroTasksProps {
  sessionId: number;
  unid: string;
  sessionFach: string;
}

// Farben für verschiedene Lernpläne (basierend auf der Original-Implementierung)
const colorList = [
  '#000000', // Schwarz
  '#0000FF', // Blau
  '#00FF00', // Grün
  '#FF0000', // Rot
  '#FFFF00', // Gelb
  '#FF00FF', // Pink/Magenta
  '#00FFFF', // Aqua/Cyan
  '#00FF00', // Hellgrün
  '#800000', // Dunkelrot
  '#808000', // Olivgrün
  '#000080', // Dunkelblau
  '#800080', // Lila
  '#008080', // Petrol/Türkis
  '#808080', // Grau
  '#C0C0C0', // Hellgrau
  '#FFFFFF', // Weiß
];

export default function LernbueroTasks({ sessionId, unid, sessionFach }: LernbueroTasksProps) {
  const queryClient = useQueryClient();
  const prevBatchCompletedCountRef = useRef<number | null>(null);
  const [activeFach, setActiveFach] = useState<PanelBQuickSubject>(() => {
    if (PANEL_B_QUICK_SUBJECTS.includes(sessionFach as PanelBQuickSubject)) {
      return sessionFach as PanelBQuickSubject;
    }
    return 'Deutsch';
  });
  // State für den Fortschritt der Aufgaben
  const [taskProgress, setTaskProgress] = useState<Map<number, boolean>>(new Map());

  useEffect(() => {
    if (PANEL_B_QUICK_SUBJECTS.includes(sessionFach as PanelBQuickSubject)) {
      setActiveFach(sessionFach as PanelBQuickSubject);
    }
  }, [sessionFach, unid]);

  const subjectSwitcher = (
    <div className="card flex flex-col gap-3 p-3 sm:flex-row sm:items-center sm:justify-between">
      <div className="min-w-0 text-sm text-gray-600 dark:text-gray-400">
        {activeFach !== sessionFach ? (
          <p>
            <span className="font-medium text-amber-800 dark:text-amber-200">Temporär: {activeFach}</span>
            <span className="text-gray-500 dark:text-gray-500"> · Sitzung: {sessionFach}</span>
          </p>
        ) : (
          <p>
            Fach der Sitzung: <span className="font-medium text-gray-900 dark:text-gray-100">{activeFach}</span>
          </p>
        )}
      </div>
      <div className="flex flex-wrap gap-1" role="group" aria-label="Fach für Aufgaben-Kacheln">
        {PANEL_B_QUICK_SUBJECTS.map((subject) => (
          <Button
            key={subject}
            type="button"
            size="sm"
            variant={activeFach === subject ? 'primary' : 'outline'}
            onClick={() => setActiveFach(subject)}
            aria-pressed={activeFach === subject}
          >
            {subject}
          </Button>
        ))}
      </div>
    </div>
  );

  // Query für die Aufgaben des Schülers
  const { data: tasksData, isLoading, error, refetch } = useQuery<TasksData>(
    ['lernbuero-tasks', sessionId, unid, activeFach],
    async () => {
      const response = await api.get(`/lernbuero/tasks/${sessionId}/${unid}`, {
        params: { fach: activeFach }
      });
      return response.data.data;
    },
    {
      enabled: !!sessionId && !!unid,
      retry: 3,
      onError: (error) => {
        console.error('Fehler beim Laden der Aufgaben:', error);
        toast.error('Fehler beim Laden der Aufgaben');
      }
    }
  );

  // NEU: Batch-Query für alle Task-Status
  const { data: batchTaskStatus, refetch: refetchBatchStatus } = useQuery(
    ['task-status-batch', sessionId, unid, activeFach],
    async () => {
      console.log('🔄 Fetching batch task status...', { sessionId, unid, activeFach });
      const response = await api.get(`/lernbuero/task-status-batch/${sessionId}/${unid}`, {
        params: { fach: activeFach }
      });
      console.log('📥 Batch response received:', response.data);
      return response.data.data;
    },
    {
      enabled: !!sessionId && !!unid,
      refetchInterval: 5000, // Polling alle 5 Sekunden
      refetchIntervalInBackground: false,
      retry: 3,
      onSuccess: (data) => {
        console.log('✅ Batch query success:', data);
      },
      onError: (error) => {
        console.error('❌ Fehler beim Laden der Batch-Task-Status:', error);
      }
    }
  );

  // Update Task-Progress: Batch-Status hat Vorrang, sonst Initial-Daten aus tasksData
  useEffect(() => {
    if (!tasksData?.tasks) {
      return;
    }

    const updatedProgress = new Map<number, boolean>();
    
    // Alle Aufgaben als nicht erledigt initialisieren
    tasksData.tasks.forEach(task => {
      const normalizedAid = Number(task.AID);
      if (!Number.isNaN(normalizedAid)) {
        updatedProgress.set(normalizedAid, false);
      }
    });
    
    // Priorisiere Batch-Status (von aktiven Lernplänen)
    if (batchTaskStatus?.completedTaskIds) {
      batchTaskStatus.completedTaskIds.forEach((aid: number) => {
        const normalizedAid = Number(aid);
        if (!Number.isNaN(normalizedAid) && updatedProgress.has(normalizedAid)) {
          updatedProgress.set(normalizedAid, true);
        }
      });
      
      setTaskProgress(updatedProgress);
    } 
    // Fallback: Initial-Daten aus tasksData (wenn Batch noch nicht geladen)
    else if (tasksData.completedTasks) {
      tasksData.completedTasks.forEach((aid: number) => {
        const normalizedAid = Number(aid);
        if (!Number.isNaN(normalizedAid) && updatedProgress.has(normalizedAid)) {
          updatedProgress.set(normalizedAid, true);
        }
      });
      
      setTaskProgress(updatedProgress);
    } else {
      // Keine completed tasks
      setTaskProgress(updatedProgress);
    }
  }, [batchTaskStatus, tasksData]);

  // Statistik aktualisieren, wenn Batch-Polling neue Belege meldet (z. B. anderer Lehrer/Tab)
  useEffect(() => {
    const completedCount = batchTaskStatus?.completedTaskIds?.length ?? 0;
    if (
      prevBatchCompletedCountRef.current !== null &&
      prevBatchCompletedCountRef.current !== completedCount
    ) {
      void queryClient.invalidateQueries(['student-progress', unid]);
      void queryClient.invalidateQueries(['lernbuero-attendance', sessionId]);
    }
    prevBatchCompletedCountRef.current = completedCount;
  }, [batchTaskStatus?.completedTaskIds, unid, sessionId, queryClient]);

  // Diese Funktionen werden nicht mehr benötigt, da sie in TaskTile verschoben wurden

  const getTaskColor = (lpid: number) => {
    const planIndex = tasksData?.learningPlans.findIndex(plan => plan.LPID === lpid);
    if (planIndex === undefined || planIndex === -1) {
      return '#808080'; // Fallbackfarbe für unbekannte Lernpläne
    }
    return colorList[planIndex % colorList.length];
  };

  const getPlanName = (lpid: number) => {
    const plan = tasksData?.learningPlans.find(plan => plan.LPID === lpid);
    if (!plan) {
      return 'Unbekannter Plan';
    }
    return plan.NAME;
  };

  // Callback für Status-Änderungen (Beleg vergeben / zurückgezogen)
  const handleStatusChange = (aid: number, isCompleted: boolean) => {
    setTaskProgress(prev => {
      const next = new Map(prev);
      const normalizedAid = Number(aid);
      if (!Number.isNaN(normalizedAid)) {
        next.set(normalizedAid, isCompleted);
      }
      return next;
    });

    void queryClient.invalidateQueries(['student-progress', unid]);
    void queryClient.invalidateQueries(['lernbuero-tasks', sessionId, unid, activeFach]);
    void queryClient.invalidateQueries(['task-status-batch', sessionId, unid, activeFach]);
    void queryClient.invalidateQueries(['lernbuero-attendance', sessionId]);
  };

  // Berechne den Fortschritt ausschließlich anhand der geladenen Aufgaben
  const taskList = tasksData?.tasks || [];
  const completedTasks = taskList.reduce((count, task) => {
    const normalizedAid = Number(task.AID);
    if (Number.isNaN(normalizedAid)) {
      return count;
    }
    return count + (taskProgress.get(normalizedAid) ? 1 : 0);
  }, 0);
  const totalTasks = taskList.length;
  const progressPercentage = totalTasks > 0 ? Math.round((completedTasks / totalTasks) * 100) : 0;

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('de-DE');
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        {subjectSwitcher}
        <div className="flex items-center justify-center py-12">
          <RefreshCw className="h-8 w-8 animate-spin text-primary-600 dark:text-primary-400" />
          <span className="ml-3 text-sm text-gray-600 dark:text-gray-400">Lade Aufgaben…</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-4">
        {subjectSwitcher}
        <div className="flex flex-col items-center justify-center py-12">
          <AlertTriangle className="mb-4 h-12 w-12 text-red-500 dark:text-red-400" />
          <h3 className="mb-2 text-lg font-semibold text-gray-900 dark:text-gray-50">Fehler beim Laden</h3>
          <p className="mb-4 text-center text-sm text-gray-600 dark:text-gray-400">
            Die Aufgaben konnten nicht geladen werden.
          </p>
          <Button type="button" variant="primary" onClick={() => refetch()}>
            Erneut versuchen
          </Button>
        </div>
      </div>
    );
  }

  if (!tasksData || tasksData.tasks.length === 0) {
    return (
      <div className="space-y-4">
        {subjectSwitcher}
        <div className="flex flex-col items-center justify-center py-12">
          <BookOpen className="mb-4 h-16 w-16 text-gray-300 dark:text-gray-600" />
          <h3 className="mb-2 text-lg font-semibold text-gray-900 dark:text-gray-50">Keine Aufgaben verfügbar</h3>
          <p className="max-w-md text-center text-sm text-gray-600 dark:text-gray-400">
            Für diesen Schüler sind aktuell keine Aufgaben im Fach {activeFach} verfügbar.
            {activeFach !== sessionFach ? ' Wählen Sie oben ein anderes Fach oder kehren Sie zur Sitzung zurück.' : ' Probieren Sie ein anderes Fach über die Schnellauswahl.'}
          </p>
        </div>
        <SubjectProgressOverview unid={unid} />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {subjectSwitcher}
      {/* Aufgaben Grid - Prominent und weit oben */}
      <div className="grid grid-cols-3 md:grid-cols-4 lg:grid-cols-6 xl:grid-cols-8 gap-3">
        {tasksData.tasks.map((task) => {
          const taskColor = getTaskColor(task.LPID);
          const normalizedAid = Number(task.AID);
          const taskIsCompleted = normalizedAid && !Number.isNaN(normalizedAid)
            ? Boolean(taskProgress.get(normalizedAid))
            : false;
          
          return (
            <TaskTile
              key={task.AID}
              task={task}
              sessionId={sessionId}
              unid={unid}
              taskColor={taskColor}
              isCompleted={taskIsCompleted}
              onStatusChange={handleStatusChange}
            />
          );
        })}
      </div>

      {/* Legende - Direkt unter den Kacheln */}
      {tasksData.learningPlans.length > 0 && (
        <div className="card p-4">
          <h4 className="mb-2 text-sm font-semibold text-gray-900 dark:text-gray-50">Lernpläne</h4>
          <div className="flex flex-wrap gap-2">
            {tasksData.learningPlans.map((plan, index) => (
              <Link
                key={plan.LPID}
                to={`/building-blocks?lpid=${plan.LPID}`}
                title={`Lernplan „${plan.NAME}" in Bausteine öffnen`}
                className="flex max-w-xs items-center gap-1.5 rounded-md text-xs text-primary-700 underline-offset-2 hover:text-primary-900 hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-500 dark:text-primary-300 dark:hover:text-primary-200"
              >
                <span
                  className="h-3 w-3 shrink-0 rounded-full ring-1 ring-gray-300 dark:ring-gray-600"
                  style={{ backgroundColor: colorList[index % colorList.length] }}
                  aria-hidden
                />
                <span className="truncate">{plan.NAME}</span>
              </Link>
            ))}
          </div>
        </div>
      )}

      <div className="card flex flex-col gap-3 p-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="text-sm font-medium text-primary-900 dark:text-primary-200">
          Fortschritt: {completedTasks}/{totalTasks} Aufgaben ({progressPercentage}%)
        </div>
        <div className="h-2 min-w-[6rem] flex-1 rounded-full bg-primary-200 dark:bg-primary-900/60 sm:max-w-xs">
          <div
            className="h-2 rounded-full bg-primary-600 transition-all duration-300 dark:bg-primary-500"
            style={{ width: `${progressPercentage}%` }}
          />
        </div>
      </div>

      {/* Fächerübergreifende Fortschritts-Übersicht */}
      <SubjectProgressOverview unid={unid} />
    </div>
  );
}
